'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMarketplaceSource,
  useLocale,
  useBlockPart,
  PortalLink,
  type MarketplaceBankTransferDetails,
  type MarketplaceOrder,
} from '@akademi/site-builder-runtime';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@akademi/ui';
import { Check, Copy, CreditCard, Loader2, Wifi } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { mpSurface, formatMarketPrice } from './_shared';

export interface MarketplaceBankTransferInstructionsProps {
  /** Bound from the route's `[purchaseOrderId]` segment (see
   *  `bindPathParams.ts`'s `MarketplaceBankTransferInstructions` entry in
   *  the portal) — leave empty in seed data so the real value always wins. */
  purchaseOrderId?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const LAYOUT_CSS = `
.mp-bt-card-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--space-4, 16px); }
`;

function useCopy(value?: string) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard permission denied — nothing to fall back to.
    }
  }
  return { copied, handleCopy };
}

/** Plain label/value row with a trailing copy button — the secondary
 *  details that don't fit on the card face (account number, sort code,
 *  reference). */
function CopyRow({ label, value }: { label: string; value?: string }) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const { copied, handleCopy } = useCopy(value);
  if (!value) return null;

  return (
    <div
      {...part('field')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3, 12px)',
        padding: 'var(--space-3, 12px)',
        borderRadius: 'var(--radius-md, 8px)',
        background: 'var(--color-surface-muted, #f4f4f5)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-tertiary, #71717a)',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-md, 15px)',
            fontWeight: 600,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={handleCopy}
        aria-label={t('marketplace.bankTransfer.copy', { defaultValue: 'Copy' })}
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </Button>
    </div>
  );
}

/** IBAN copy button styled to sit on the dark card face — white icon over
 *  the card's own gradient, not the neutral card-surface `Button`. */
function CardCopyButton({ value, label }: { value?: string; label: string }) {
  const { copied, handleCopy } = useCopy(value);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-md, 8px)',
        border: '1px solid rgba(255,255,255,0.28)',
        background: 'rgba(255,255,255,0.12)',
        color: '#fff',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

/**
 * Bank-transfer (EFT) payment instructions — akademi-lms-v2's
 * `/marketplace/payment/bank_transfer/[purchaseOrderId]` page. With a
 * {@link useMarketplaceSource} mounted, fetches the bank account details AND
 * the order total from the one
 * `POST /marketplace/payment/bank_transfer/create-payment-gateway-url` call
 * (resumed via `purchaseOrderId`) — mirroring the source app, which never
 * makes a second `purchase-order/{id}` call on this page; with no source,
 * or no `purchaseOrderId`, renders an inert preview.
 *
 * A single self-contained card — status/line-items/pay-actions live on
 * {@link MarketplaceCheckoutCta}'s own `mode="order"` (with
 * `showCartDetail`) instead; the two are meant to sit side by side (see
 * the `marketplace-payment` reserved page).
 */
export function MarketplaceBankTransferInstructions({
  purchaseOrderId,
  paddingOuter,
  paddingInner,
}: MarketplaceBankTransferInstructionsProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const source = useMarketplaceSource();
  const part = useBlockPart();

  const [bankDetails, setBankDetails] = useState<MarketplaceBankTransferDetails | null>(null);
  const [reference, setReference] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<MarketplaceOrder | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    source && purchaseOrderId ? 'loading' : 'ready',
  );

  useEffect(() => {
    if (!source || !purchaseOrderId) return;
    let cancelled = false;
    setStatus('loading');
    source
      .getPaymentGatewayRedirectUrl('bank_transfer', purchaseOrderId)
      .then((redirect) => {
        if (cancelled) return;
        setBankDetails(redirect.bankDetails ?? null);
        setReference(redirect.reference);
        setOrder(redirect.order ?? null);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [source, purchaseOrderId]);

  const totalText = order?.total ? formatMarketPrice(order.total, locale) : undefined;
  const isLoading = Boolean(source && purchaseOrderId) && status === 'loading';

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <style dangerouslySetInnerHTML={{ __html: LAYOUT_CSS }} />
      <Card
        style={{
          ...mpSurface,
          ...resolvePaddingStyle(paddingInner),
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <CardHeader className="p-0">
          <CardTitle>
            {t('marketplace.bankTransfer.title', { defaultValue: 'Bank transfer (EFT)' })}
            {purchaseOrderId ? ` — #${purchaseOrderId}` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent {...part('details')} className="p-0">
          {isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 'var(--space-8, 32px)',
              }}
            >
              <Loader2 size={24} className="animate-spin" />
            </div>
          )}

          {status === 'error' && (
            <p style={{ color: 'var(--color-text-error, #b91c1c)' }}>
              {t('marketplace.bankTransfer.error', {
                defaultValue: "Couldn't load the transfer details. Please try again.",
              })}
            </p>
          )}

          {!isLoading && status !== 'error' && (
            <>
              {/* Card-face visual — the details someone actually types into
                    their bank's transfer form (bank + IBAN + receiver), styled
                    like a physical card so they read as one unit at a glance. */}
              <div
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 'var(--radius-xl, 20px)',
                  padding: 'var(--space-5, 20px)',
                  marginBottom: 'var(--space-5, 20px)',
                  background:
                    'linear-gradient(135deg, var(--color-brand-500, #319db3), var(--color-brand-800, #0f3d47) 65%, var(--color-neutral-950, #0a0f10))',
                  boxShadow:
                    '0 20px 40px -16px color-mix(in srgb, var(--color-brand-600, #227e91) 60%, transparent)',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-5, 20px)',
                  minHeight: 180,
                }}
              >
                {/* Diagonal shine sweep — the "premium card" cue, purely decorative. */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(circle at 85% -10%, rgba(255,255,255,0.35), transparent 45%), linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.14) 48%, transparent 60%)',
                    pointerEvents: 'none',
                  }}
                />

                <div
                  aria-hidden
                  style={{
                    position: 'relative',
                    width: 34,
                    height: 24,
                    borderRadius: 5,
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.55), rgba(255,255,255,0.15))',
                    border: '1px solid rgba(255,255,255,0.4)',
                  }}
                />

                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-3, 12px)',
                    marginTop: 'calc(-1 * var(--space-3, 12px))',
                  }}
                >
                  {bankDetails?.bankLogoUrl ? (
                    <img
                      src={bankDetails.bankLogoUrl}
                      alt={bankDetails.bank ?? ''}
                      style={{ maxHeight: 28, filter: 'brightness(0) invert(1)' }}
                    />
                  ) : (
                    <span
                      style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}
                    >
                      <CreditCard size={20} />
                      {bankDetails?.bank ??
                        t('marketplace.bankTransfer.bank', { defaultValue: 'Bank' })}
                    </span>
                  )}
                  <Wifi size={20} style={{ opacity: 0.6, transform: 'rotate(90deg)' }} />
                </div>

                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs, 12px)',
                      opacity: 0.7,
                      marginBottom: 4,
                    }}
                  >
                    IBAN
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--space-3, 12px)',
                    }}
                  >
                    <span
                      {...part('iban')}
                      style={{
                        fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                        fontSize: 'var(--font-size-lg, 19px)',
                        letterSpacing: '0.05em',
                        fontWeight: 600,
                        wordBreak: 'break-all',
                      }}
                    >
                      {bankDetails?.iban ?? 'TR•• •••• •••• •••• •••• •••• ••'}
                    </span>
                    <CardCopyButton
                      value={bankDetails?.iban}
                      label={t('marketplace.bankTransfer.copy', { defaultValue: 'Copy' })}
                    />
                  </div>
                </div>

                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--space-4, 16px)',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs, 12px)',
                        opacity: 0.7,
                        marginBottom: 2,
                      }}
                    >
                      {t('marketplace.bankTransfer.receiver', { defaultValue: 'Recipient name' })}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-md, 15px)', fontWeight: 600 }}>
                      {bankDetails?.receiver ?? '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs, 12px)',
                        opacity: 0.7,
                        marginBottom: 2,
                      }}
                    >
                      {t('marketplace.bankTransfer.amount', { defaultValue: 'Amount to transfer' })}
                    </div>
                    <div
                      style={{
                        display: 'inline-flex',
                        fontSize: 'var(--font-size-lg, 19px)',
                        fontWeight: 800,
                        padding: '5px 14px',
                        borderRadius: 999,
                        background: '#fff',
                        color: 'var(--color-brand-700, #185a68)',
                        boxShadow: '0 4px 10px -4px rgba(0,0,0,0.35)',
                      }}
                    >
                      {totalText ?? '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mp-bt-card-details">
                <CopyRow
                  label={t('marketplace.bankTransfer.accountNumber', {
                    defaultValue: 'Account number',
                  })}
                  value={bankDetails?.accountNumber}
                />
                <CopyRow
                  label={t('marketplace.bankTransfer.sortCode', { defaultValue: 'Sort code' })}
                  value={bankDetails?.sortCode}
                />
                <CopyRow
                  label={t('marketplace.bankTransfer.reference', {
                    defaultValue: 'Payment description / reference',
                  })}
                  value={reference}
                />
              </div>

              {!bankDetails && (
                <p
                  style={{
                    color: 'var(--color-text-tertiary, #71717a)',
                    marginTop: 'var(--space-3, 12px)',
                  }}
                >
                  {t('marketplace.bankTransfer.preview', {
                    defaultValue: 'Bank account details will appear here once payment is set up.',
                  })}
                </p>
              )}

              <div
                style={{
                  marginTop: 'var(--space-4, 16px)',
                  padding: 'var(--space-3, 12px)',
                  borderRadius: 'var(--radius-md, 8px)',
                  borderLeft: '3px solid var(--color-brand-500, #319db3)',
                  background: 'var(--color-surface-muted, #f4f4f5)',
                  fontSize: 'var(--font-size-sm, 13px)',
                  color: 'var(--color-text-secondary, #52525b)',
                }}
              >
                {t('marketplace.bankTransfer.note', {
                  defaultValue:
                    'Transfer the amount above to the account details, using the reference as the payment description. Your order confirms automatically once the transfer clears — check its status any time on ',
                })}
                <PortalLink href="/marketplace/cart" style={{ fontWeight: 600 }}>
                  {t('marketplace.bankTransfer.ordersLink', { defaultValue: 'my orders' })}
                </PortalLink>
                .
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </SectionShell>
  );
}
